import { ChevronDown, ChevronRight, Download, Edit3 } from "lucide-react";
import React, { useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { cn } from "@repo/core";
import { AppCard, KpiCard, KpiIcons, Money, ProgressBar, SectionTitle } from "../components/ui/AppCard";
import { AppTable } from "../components/ui/AppTable";
import { ChartExportFrame } from "../components/ChartExportFrame";
import { useCurrency } from "../hooks/useCurrency";
import { useDebtCalculator, useDebts } from "../hooks/useBudgetData";
import { useT } from "../i18n/useT";
import { db } from "../lib/db";
import { handleFormSubmit, onEnterToSubmitKeyDown } from "../lib/formKeyboard";
import { exportDebtAmortization } from "../lib/pdf/exportFromDb";
import {
  exportCanvasChartPng,
  resolveThemeChartColors,
} from "../lib/exportUtils";
import type { Debt, DebtResult, PayoffMethod } from "../lib/schema";

const PASTEL_COLORS = [
  { light: "bg-rose-50   border-rose-200   dark:bg-rose-950/20   dark:border-rose-800",   bar: "#f87171" },
  { light: "bg-violet-50 border-violet-200 dark:bg-violet-950/20 dark:border-violet-800", bar: "#a78bfa" },
  { light: "bg-sky-50    border-sky-200    dark:bg-sky-950/20    dark:border-sky-800",    bar: "#38bdf8" },
  { light: "bg-emerald-50 border-emerald-200 dark:bg-emerald-950/20 dark:border-emerald-800", bar: "#34d399" },
  { light: "bg-amber-50  border-amber-200  dark:bg-amber-950/20  dark:border-amber-800",  bar: "#fbbf24" },
  { light: "bg-pink-50   border-pink-200   dark:bg-pink-950/20   dark:border-pink-800",   bar: "#f472b6" },
];

const TOOLTIP_STYLE = {
  border: "1px solid hsl(var(--border))",
  borderRadius: "var(--radius)",
  fontSize: 11,
  fontWeight: 600,
  background: "hsl(var(--card))",
  color: "hsl(var(--card-foreground))",
  boxShadow: "var(--card-shadow)",
};

function formatMonth(m: Date, locale: string): string {
  return m.toLocaleDateString(locale, { month: "short", year: "2-digit" });
}

function DebtCard({
  result,
  idx,
  scheduleOpen,
  onToggleSchedule,
}: {
  result: DebtResult;
  idx: number;
  scheduleOpen: boolean;
  onToggleSchedule: () => void;
}) {
  const { format } = useCurrency();
  const { t, locale } = useT();
  const { debt, totalInterest, payoffDate, monthsToPayoff, schedule } = result;
  const palette = PASTEL_COLORS[idx % PASTEL_COLORS.length];
  const paidPct = 100 - (debt.balance / (debt.balance + totalInterest)) * 100;

  return (
    <AppCard padding="none" className="overflow-hidden">
      <div className={cn("px-4 py-3 border-b border-border flex items-start justify-between gap-2", palette.light)}>
        <div className="min-w-0">
          <p className="font-semibold text-sm text-foreground truncate">{debt.name}</p>
          <p className="text-[10px] text-muted-foreground">{t("debt.apr", { n: debt.interestRate })}</p>
        </div>
        <div className="text-right shrink-0">
          <Money amount={debt.balance} size="md" />
          <p className="text-[10px] text-muted-foreground">{t("debt.currentBalance")}</p>
        </div>
      </div>
      <div className="px-4 py-3 space-y-3">
        <div className="grid grid-cols-3 gap-2 text-center">
          {[
            { label: t("debt.minPay"), value: debt.minPayment },
            { label: t("debt.extra"), value: debt.extraPayment },
            { label: t("debt.interest"), value: totalInterest },
          ].map(({ label, value }) => (
            <div key={label} className="min-w-0">
              <p className="text-[9px] font-semibold uppercase tracking-widest text-muted-foreground">{label}</p>
              <Money amount={value} className="text-sm" />
            </div>
          ))}
        </div>
        <div className="border-t border-border pt-2.5 space-y-2">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
            <div>
              <p className="text-[9px] text-muted-foreground">{t("debt.debtFreeDate")}</p>
              <p className="font-semibold text-sm">{formatMonth(payoffDate, locale)}</p>
              <p className="text-[9px] text-muted-foreground">{t("debt.months", { n: monthsToPayoff })}</p>
            </div>
            <ProgressBar value={paidPct} showPct height={6} className="w-full sm:w-32" />
          </div>
        </div>

        <button
          type="button"
          onClick={onToggleSchedule}
          className="w-full min-h-[44px] flex items-center justify-center gap-1.5 text-xs font-medium text-primary border border-border rounded-lg hover:bg-accent transition-colors"
        >
          {scheduleOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          {scheduleOpen ? t("debt.hideSchedule") : t("debt.viewSchedule")}
        </button>

        {scheduleOpen && (
          <div className="scroll-hint-x -mx-1 px-1">
            <AppTable
              columns={[
                { key: "month", label: t("debt.col.month"), className: "w-16 font-mono text-xs" },
                { key: "payment", label: t("debt.col.payment"), align: "right", render: (r) => format(r.payment) },
                { key: "principal", label: t("debt.col.principal"), align: "right", render: (r) => format(r.principal) },
                { key: "interest", label: t("debt.col.interest"), align: "right", render: (r) => format(r.interest) },
                { key: "balance", label: t("debt.col.balance"), align: "right", render: (r) => format(r.balance) },
              ]}
              data={schedule}
              keyProp="month"
              emptyMessage={t("common.noData")}
              className="!shadow-none"
            />
          </div>
        )}
      </div>
    </AppCard>
  );
}

function EditDebtForm({ debt, onClose }: { debt: Debt; onClose: () => void }) {
  const { symbol } = useCurrency();
  const { t } = useT();
  const [form, setForm] = useState({
    ...debt,
    balanceStr: String(debt.balance),
    rateStr: String(debt.interestRate),
    minStr: String(debt.minPayment),
    extraStr: String(debt.extraPayment),
  });

  const handleSave = async () => {
    await db.debts.update(debt.id, {
      balance: parseFloat(form.balanceStr) || debt.balance,
      interestRate: parseFloat(form.rateStr) || debt.interestRate,
      minPayment: parseFloat(form.minStr) || debt.minPayment,
      extraPayment: parseFloat(form.extraStr) || 0,
    });
    onClose();
  };

  return (
    <AppCard color="lemon" padding="md">
      <form
        onKeyDown={onEnterToSubmitKeyDown}
        onSubmit={(e) => handleFormSubmit(e, () => handleSave())}
      >
      <p className="font-semibold text-sm mb-3">{debt.name}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {[
          { label: t("debt.balance", { symbol }), key: "balanceStr" },
          { label: t("debt.rate"), key: "rateStr" },
          { label: t("debt.minPayment"), key: "minStr" },
          { label: t("debt.extraPayment"), key: "extraStr" },
        ].map(({ label, key }) => (
          <div key={key}>
            <label className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground block mb-1">
              {label}
            </label>
            <input
              type="number"
              step="0.01"
              value={String((form as unknown as Record<string, unknown>)[key] ?? "")}
              onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
              className="app-input min-h-[44px] sm:min-h-0"
            />
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-3">
        <button type="submit" className="app-btn min-h-[44px] sm:min-h-0">
          {t("common.save")}
        </button>
        <button type="button" onClick={onClose} className="app-btn-outline min-h-[44px] sm:min-h-0">
          {t("common.cancel")}
        </button>
      </div>
      </form>
    </AppCard>
  );
}

export function DebtPayoffPage() {
  const { format, currency, locale } = useCurrency();
  const { t, locale: uiLocale } = useT();
  const [method, setMethod] = useState<PayoffMethod>("snowball");
  const results = useDebtCalculator(method);
  const debts = useDebts();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [openSchedules, setOpenSchedules] = useState<Record<string, boolean>>({});

  const totalBalance = debts.reduce((s, d) => s + d.balance, 0);
  const totalInterest = results.reduce((s, r) => s + r.totalInterest, 0);
  const lastPayoff = results.length > 0 ? results[results.length - 1].payoffDate : null;

  const maxMonths = Math.max(...results.map((r) => r.monthsToPayoff), 1);
  const chartData: { month: string; balance: number }[] = [];
  for (let m = 0; m <= Math.min(maxMonths, 120); m += m < 24 ? 3 : 6) {
    const balance = results.reduce((s, r) => {
      const row = r.schedule[m - 1];
      return s + (row?.balance ?? (m === 0 ? r.debt.balance : 0));
    }, 0);
    const dt = new Date();
    dt.setMonth(dt.getMonth() + m);
    chartData.push({ month: formatMonth(dt, uiLocale), balance: Math.round(balance) });
  }

  const chartTitle = t("debt.chartTitle");
  const balanceLabel = t("debt.col.balance");

  const exportDebtPng = () => {
    // Keep from start through last month that still has balance (or the zero payoff month)
    let last = 0;
    chartData.forEach((r, i) => {
      if (r.balance > 0 || i === 0) last = i;
    });
    const rows = chartData.slice(0, last + 1);
    const colors = resolveThemeChartColors();
    exportCanvasChartPng({
      filename: "debt-balance-over-time",
      title: chartTitle,
      labels: rows.map((r) => r.month),
      series: [{ label: balanceLabel, color: colors.destructive, values: rows.map((r) => r.balance) }],
      watermark: `BudgetOS · ${rows[0]?.month ?? ""}–${rows[rows.length - 1]?.month ?? ""}`,
      kind: "area",
      formatTick: (n) => format(n, true),
    });
  };

  return (
    <div className="p-4 lg:p-6 space-y-5 w-full max-w-full overflow-x-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <SectionTitle className="mb-0">{t("debt.title")}</SectionTitle>
        <div className="sm:ml-auto flex border border-border rounded-lg overflow-hidden w-full sm:w-auto">
          {(["snowball", "avalanche"] as PayoffMethod[]).map((m) => (
            <button
              key={m}
              onClick={() => setMethod(m)}
              className={cn(
                "flex-1 sm:flex-none px-3 py-2.5 sm:py-1.5 text-xs font-medium transition-colors min-h-[44px] sm:min-h-0",
                method === m
                  ? "bg-primary text-primary-foreground"
                  : "bg-background text-muted-foreground hover:bg-accent"
              )}
            >
              {t(`method.${m}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 min-[390px]:grid-cols-3 gap-3">
        <KpiCard
          label={t("debt.totalDebt")}
          value={format(totalBalance, true)}
          color="coral"
          icon={<KpiIcons.debts size={16} />}
          index={0}
        />
        <KpiCard
          label={t("debt.totalInterest")}
          value={format(totalInterest, true)}
          color="lemon"
          icon={<KpiIcons.rate size={16} />}
          index={1}
        />
        <KpiCard
          label={t("debt.debtFreeDate")}
          value={lastPayoff ? formatMonth(lastPayoff, uiLocale) : "—"}
          color="mint"
          icon={<KpiIcons.calendar size={16} />}
          index={2}
        />
      </div>

      <AppCard padding="md" className="overflow-hidden">
        <ChartExportFrame title={chartTitle} onExport={exportDebtPng}>
        <div className="scroll-hint-x">
          <ResponsiveContainer width="100%" height={180} minWidth={280}>
            <AreaChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: 4 }}>
              <defs>
                <linearGradient id="debtGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--destructive))" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="hsl(var(--destructive))" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 9, fill: "hsl(var(--muted-foreground))" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                width={68}
                tick={{ fontSize: 9, fill: "hsl(var(--muted-foreground))" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => format(v, true)}
              />
              <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v: number) => [format(v), balanceLabel]} />
              <Area
                type="monotone"
                dataKey="balance"
                stroke="hsl(var(--destructive))"
                strokeWidth={2}
                fill="url(#debtGrad)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        </ChartExportFrame>
      </AppCard>

      <div className="space-y-3">
        <p className="app-section-title">
          {method === "snowball" ? t("debt.payoffOrderSnowball") : t("debt.payoffOrderAvalanche")}
        </p>
        {results.length === 0 ? (
          <AppCard padding="md">
            <p className="text-sm text-muted-foreground text-center py-6">
              {t("empty.debts")}
            </p>
          </AppCard>
        ) : (
        results.map((r, i) => (
          <div key={r.debt.id}>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-semibold shrink-0">
                {i + 1}
              </div>
              <button
                onClick={() => setEditingId(editingId === r.debt.id ? null : r.debt.id)}
                className="app-btn-outline text-[10px] px-3 py-2 gap-1 min-h-[44px] sm:min-h-0"
              >
                <Edit3 size={10} />
                {t("debt.edit")}
              </button>
              <button
                type="button"
                onClick={() =>
                  exportDebtAmortization(r.debt, {
                    currency,
                    locale,
                    uiLocale,
                    method,
                    allDebts: debts,
                  })
                }
                className="app-btn-outline text-[10px] px-3 py-2 gap-1 min-h-[44px] sm:min-h-0 no-print"
              >
                <Download size={10} />
                {t("debt.exportSchedule")}
              </button>
            </div>
            {editingId === r.debt.id && (
              <div className="mb-2">
                <EditDebtForm debt={r.debt} onClose={() => setEditingId(null)} />
              </div>
            )}
            <DebtCard
              result={r}
              idx={i}
              scheduleOpen={!!openSchedules[r.debt.id]}
              onToggleSchedule={() =>
                setOpenSchedules((prev) => ({
                  ...prev,
                  [r.debt.id]: !prev[r.debt.id],
                }))
              }
            />
          </div>
        ))
        )}
      </div>
    </div>
  );
}
